import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentMethods = ({ paymentMethods, onAddPaymentMethod, onRemovePaymentMethod, onSetDefault }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    }
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    if (name?.startsWith('billing.')) {
      const field = name?.split('.')?.[1];
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev?.billingAddress,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.cardNumber?.replace(/\s/g, '')) {
      newErrors.cardNumber = 'Card number is required';
    } else if (formData?.cardNumber?.replace(/\s/g, '')?.length < 13) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }
    
    if (!formData?.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    }
    
    if (!formData?.cvv) {
      newErrors.cvv = 'CVV is required';
    }
    
    if (!formData?.cardholderName?.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleAddCard = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await onAddPaymentMethod(formData);
      setShowAddForm(false);
      setFormData({
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        cardholderName: '',
        billingAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US'
        }
      });
    } catch (error) {
      setErrors({ general: 'Failed to add payment method. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCard = async (cardId) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      await onRemovePaymentMethod(cardId);
    }
  };

  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa': return 'CreditCard';
      case 'mastercard': return 'CreditCard';
      case 'amex': return 'CreditCard';
      default: return 'CreditCard';
    }
  };

  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e?.target?.value);
    setFormData(prev => ({ ...prev, cardNumber: formatted }));
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Icon name="CreditCard" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Payment Methods</h2>
            <p className="text-sm text-muted-foreground">Manage your saved payment methods</p>
          </div>
        </div>
        {!showAddForm && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setShowAddForm(true)}
          >
            Add Card
          </Button>
        )}
      </div>
      {/* Existing Payment Methods */}
      <div className="space-y-4 mb-6">
        {paymentMethods?.map((method) => (
          <div
            key={method?.id}
            className={`border rounded-lg p-4 ${
              method?.isDefault ? 'border-primary bg-primary/5' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={getCardIcon(method?.brand)} size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-foreground">
                      •••• •••• •••• {method?.last4}
                    </p>
                    {method?.isDefault && (
                      <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {method?.brand} • Expires {method?.expiryMonth}/{method?.expiryYear}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {!method?.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSetDefault(method?.id)}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleRemoveCard(method?.id)}
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
        
        {paymentMethods?.length === 0 && !showAddForm && (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">No payment methods saved</p>
            <p className="text-sm text-muted-foreground">
              Add a payment method for faster checkout
            </p>
          </div>
        )}
      </div>
      {/* Add Payment Method Form */}
      {showAddForm && (
        <div className="border border-border rounded-lg p-6 bg-muted/20">
          <h3 className="text-lg font-medium text-foreground mb-4">Add New Payment Method</h3>
          
          {errors?.general && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error">{errors?.general}</p>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Card Number"
              type="text"
              name="cardNumber"
              value={formData?.cardNumber}
              onChange={handleCardNumberChange}
              error={errors?.cardNumber}
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                name="expiryDate"
                value={formData?.expiryDate}
                onChange={handleInputChange}
                error={errors?.expiryDate}
                placeholder="MM/YY"
                maxLength={5}
                required
              />
              <Input
                label="CVV"
                type="text"
                name="cvv"
                value={formData?.cvv}
                onChange={handleInputChange}
                error={errors?.cvv}
                placeholder="123"
                maxLength={4}
                required
              />
            </div>

            <Input
              label="Cardholder Name"
              type="text"
              name="cardholderName"
              value={formData?.cardholderName}
              onChange={handleInputChange}
              error={errors?.cardholderName}
              placeholder="John Doe"
              required
            />

            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">Billing Address</h4>
              <div className="space-y-4">
                <Input
                  label="Street Address"
                  type="text"
                  name="billing.street"
                  value={formData?.billingAddress?.street}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="City"
                    type="text"
                    name="billing.city"
                    value={formData?.billingAddress?.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                  />
                  <Input
                    label="State"
                    type="text"
                    name="billing.state"
                    value={formData?.billingAddress?.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                  />
                </div>
                
                <Input
                  label="ZIP Code"
                  type="text"
                  name="billing.zipCode"
                  value={formData?.billingAddress?.zipCode}
                  onChange={handleInputChange}
                  placeholder="10001"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="default"
                onClick={handleAddCard}
                loading={isLoading}
                iconName="Plus"
                className="flex-1"
              >
                Add Payment Method
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddForm(false)}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-success mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Secure Payment Processing</p>
            <p>
              Your payment information is encrypted and securely processed by Stripe. 
              We never store your full card details on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;